import { useEffect } from 'react'
import styled from 'styled-components'
import Colors from '../../assets/styles/Colors'
import api from '../../api'
import { useTranslation } from 'react-i18next'
import { FileText, Download, ArrowLeft } from 'lucide-react'

interface AILibraryProps {
  onBack: () => void
  isVisible: boolean
}

const LibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  max-width: 850px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  overflow-y: auto;
`

const LibraryHeader = styled.div`
  margin-bottom: 1.5rem;
`

const HeaderTop = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  margin-bottom: 0.5rem;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: ${Colors.lightRed};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${Colors.primaryRed};

  &:hover {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

const LibraryTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${Colors.Black};
  margin: 0;
`

const LibrarySubtitle = styled.p`
  font-size: 14px;
  color: ${Colors.primaryGrey};
  margin-left: 3.3rem;
`

const LibraryContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${Colors.white};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 8px;
  text-decoration: none;
  color: ${Colors.Black};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.primaryRed};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${Colors.lightRed};
  border-radius: 8px;
  flex-shrink: 0;
`

const FileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.Black};
  word-break: break-word;
`

const FileSize = styled.div`
  font-size: 12px;
  color: ${Colors.primaryGrey};
`

const DownloadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${Colors.lightRed};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${FileCard}:hover & {
    background: ${Colors.primaryRed};
    color: ${Colors.white};
  }
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: ${Colors.primaryGrey};
  padding: 2rem;
`

const LoadingState = styled(EmptyState)`
  font-size: 16px;
`

const ErrorState = styled(EmptyState)`
  color: ${Colors.primaryRed};
`

export default function AILibrary({ onBack, isVisible }: AILibraryProps) {
  const { t } = useTranslation()
  const { data: files, isLoading, error, refetch } = api.useGetAIFilesQuery(undefined)

  // Refetch files when the library becomes visible (user clicks Library menu item)
  useEffect(() => {
    if (isVisible) {
      refetch()
    }
  }, [isVisible, refetch])

  return (
    <LibraryContainer>
      <LibraryHeader>
        <HeaderTop>
          <BackButton onClick={onBack}>
            <ArrowLeft size={20} />
          </BackButton>
          <LibraryTitle>{t('ai.fileLibrary')}</LibraryTitle>
        </HeaderTop>
        <LibrarySubtitle>
          {files && files.length > 0 ? t('ai.filesAvailable_other', { count: files.length }) : t('ai.noFiles')}
        </LibrarySubtitle>
      </LibraryHeader>

      <LibraryContent>
        {isLoading && <LoadingState>{t('ai.loadingFiles')}</LoadingState>}

        {error && <ErrorState>{t('ai.failedLoadFiles')}</ErrorState>}

        {!isLoading && !error && files && files.length === 0 && (
          <EmptyState>
            <FileText size={48} />
            <div>{t('ai.noFiles')}</div>
          </EmptyState>
        )}

        {!isLoading && !error && files && files.length > 0 && (
          <>
            {files.map((file, index) => (
              <FileCard key={index} href={file.path} target="_blank" rel="noopener noreferrer" download={file.name}>
                <FileIconWrapper>
                  <FileText size={24} color={Colors.primaryRed} />
                </FileIconWrapper>
                <FileInfo>
                  <FileName>{file.name}</FileName>
                  <FileSize>Click to download</FileSize>
                </FileInfo>
                <DownloadIcon>
                  <Download size={16} />
                </DownloadIcon>
              </FileCard>
            ))}
          </>
        )}
      </LibraryContent>
    </LibraryContainer>
  )
}
